import { DateTime } from 'luxon'

import type { ActivationGroup } from '@/features/activationGroup/types'
import type { FormattedRow } from '@/features/bessDashboard/types'
import { BidType, MARKET_TIMEZONE, Status } from '@/features/bidding/constants'
import type { ExtendedBid, ExtendedBidPtu, PtuChunk } from '@/features/bidding/types/bid'
import type {
  BidLevelActivationGroupSymmetrical,
  BidLevelInputSymmetrical,
} from '@/features/bidding/types/bidLadderInputSymmetrical'
import type { PricingStrategyConfig } from '@/features/bidding/types/forecastPricing'
import type { HourlyPrices } from '@/features/bidding/utils/cascading/bidLadder/deriveForecastPrices'
import {
  deriveForecastPricesForDirection,
  getFallbackDailyPrice,
  getPriceForHour,
} from '@/features/bidding/utils/cascading/bidLadder/deriveForecastPrices'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'
import { megawattHoursToWattHours } from '@/utils/powerEnergyTransformations'

const HOURS_PER_DAY = 24
const VOLUME_UNIT = 'WATTS'

export type ValidationErrorType = 'MISSING_PRICE' | 'INVALID_CAPACITY' | 'MISSING_ACTIVATION_GROUP'

export type ValidationError = {
  type: ValidationErrorType
  levelId: string
  groupUuid: string
  groupCode: string
  message: string
}

export type TransformBidLevelsSymmetricalResult = {
  bids: ExtendedBid[]
  errors: ValidationError[]
}

type TransformBidLevelsSymmetricalParams = {
  levels: BidLevelInputSymmetrical[]
  deliveryDay: MarketDate
  activationGroupsMap: Map<string, ActivationGroup>
  pricingConfig?: PricingStrategyConfig
  forecastsData?: FormattedRow[]
  selectedDeliveryDay?: DateTime
}

/**
 * Transforms symmetrical bid level state into ExtendedBid objects ready for API submission.
 *
 * For symmetrical markets (FCR-N):
 * - Uses a single price per level (no UP/DOWN distinction)
 * - Creates one ExtendedBid per assigned activation group with 24 hourly PTUs
 */
export function transformBidLevelsToExtendedBidsSymmetrical({
  levels,
  deliveryDay,
  activationGroupsMap,
  pricingConfig,
  forecastsData,
  selectedDeliveryDay,
}: TransformBidLevelsSymmetricalParams): TransformBidLevelsSymmetricalResult {
  const bids: ExtendedBid[] = []
  const errors: ValidationError[] = []

  // Track which groups have been processed (assigned to levels)
  const processedGroupUuids = new Set<string>()

  for (let levelIndex = 0; levelIndex < levels.length; levelIndex++) {
    const level = levels[levelIndex]
    const levelPricingConfig = pricingConfig?.levels[levelIndex]
    const isForecastMode = levelPricingConfig?.mode === 'forecast'

    // Derive hourly prices if in forecast mode
    // For symmetrical markets, we use 'up' direction since there's no distinction
    let hourlyPrices: HourlyPrices | undefined
    if (isForecastMode && levelPricingConfig && forecastsData && selectedDeliveryDay) {
      hourlyPrices = deriveForecastPricesForDirection(
        forecastsData,
        levelPricingConfig.market,
        'up',
        levelPricingConfig.quantile,
        selectedDeliveryDay,
      )
    }

    for (const assignedGroup of level.assignedGroups) {
      processedGroupUuids.add(assignedGroup.uuid)

      const validationResult = validateGroup(level, assignedGroup, activationGroupsMap, isForecastMode, hourlyPrices)

      if (!validationResult.isValid) {
        errors.push(validationResult.error)
        continue
      }

      const { activationGroup } = validationResult

      const bid = createBidForGroup({
        assignedGroup,
        activationGroup,
        deliveryDay,
        fixedPrice: validationResult.price,
        hourlyPrices: isForecastMode ? hourlyPrices : undefined,
      })

      bids.push(bid)
    }
  }

  // Create zero bids for unassigned groups
  for (const [uuid, activationGroup] of activationGroupsMap) {
    if (!processedGroupUuids.has(uuid)) {
      const zeroBid = createZeroBidForGroup({
        activationGroup,
        deliveryDay,
      })
      bids.push(zeroBid)
    }
  }

  return { bids, errors }
}

type ValidationSuccess = {
  isValid: true
  activationGroup: ActivationGroup
  price: number
}

type ValidationFailure = {
  isValid: false
  error: ValidationError
}

function validateGroup(
  level: BidLevelInputSymmetrical,
  assignedGroup: BidLevelActivationGroupSymmetrical,
  activationGroupsMap: Map<string, ActivationGroup>,
  isForecastMode: boolean = false,
  hourlyPrices?: HourlyPrices,
): ValidationSuccess | ValidationFailure {
  // Check if activation group exists in the map
  const activationGroup = activationGroupsMap.get(assignedGroup.uuid)
  if (!activationGroup) {
    return {
      isValid: false,
      error: {
        type: 'MISSING_ACTIVATION_GROUP',
        levelId: level.id,
        groupUuid: assignedGroup.uuid,
        groupCode: assignedGroup.code,
        message: `Activation group "${assignedGroup.code}" not found`,
      },
    }
  }

  // Check if capacity is valid
  if (assignedGroup.capacityMW <= 0) {
    return {
      isValid: false,
      error: {
        type: 'INVALID_CAPACITY',
        levelId: level.id,
        groupUuid: assignedGroup.uuid,
        groupCode: assignedGroup.code,
        message: `Group "${assignedGroup.code}" has invalid capacity: ${assignedGroup.capacityMW} MW`,
      },
    }
  }

  // In forecast mode, check if we have hourly prices
  if (isForecastMode) {
    if (!hourlyPrices || hourlyPrices.prices.size === 0) {
      return {
        isValid: false,
        error: {
          type: 'MISSING_PRICE',
          levelId: level.id,
          groupUuid: assignedGroup.uuid,
          groupCode: assignedGroup.code,
          message: `No forecast prices available for group "${assignedGroup.code}"`,
        },
      }
    }

    // Check for complete hourly data
    if (!hourlyPrices.isComplete) {
      return {
        isValid: false,
        error: {
          type: 'MISSING_PRICE',
          levelId: level.id,
          groupUuid: assignedGroup.uuid,
          groupCode: assignedGroup.code,
          message: `Incomplete forecast data for group "${assignedGroup.code}" (${hourlyPrices.prices.size}/${hourlyPrices.dayHours} hours available)`,
        },
      }
    }

    const fallbackPrice = getFallbackDailyPrice(hourlyPrices)
    if (fallbackPrice === null) {
      return {
        isValid: false,
        error: {
          type: 'MISSING_PRICE',
          levelId: level.id,
          groupUuid: assignedGroup.uuid,
          groupCode: assignedGroup.code,
          message: `Unexpected error: could not calculate fallback price for group "${assignedGroup.code}"`,
        },
      }
    }
    return {
      isValid: true,
      activationGroup,
      price: fallbackPrice,
    }
  }

  // Check if price is set (fixed mode)
  if (level.price === null) {
    return {
      isValid: false,
      error: {
        type: 'MISSING_PRICE',
        levelId: level.id,
        groupUuid: assignedGroup.uuid,
        groupCode: assignedGroup.code,
        message: `Level is missing price for group "${assignedGroup.code}"`,
      },
    }
  }

  return {
    isValid: true,
    activationGroup,
    price: level.price,
  }
}

type CreateBidParams = {
  assignedGroup: BidLevelActivationGroupSymmetrical
  activationGroup: ActivationGroup
  deliveryDay: MarketDate
  fixedPrice: number
  hourlyPrices?: HourlyPrices
}

function createBidForGroup({
  assignedGroup,
  activationGroup,
  deliveryDay,
  fixedPrice,
  hourlyPrices,
}: CreateBidParams): ExtendedBid {
  const volumeInWatts = megawattHoursToWattHours(assignedGroup.capacityMW)

  const ptus = generate24HourPtus(deliveryDay, volumeInWatts, fixedPrice, hourlyPrices)

  return {
    portfolio: activationGroup.portfolio,
    deliveryDay,
    createdAt: DateTime.now().setZone(MARKET_TIMEZONE),
    marketProgram: activationGroup.marketProgram,
    status: Status.DRAFT,
    activationGroupUuid: assignedGroup.uuid,
    bidType: BidType.CAPACITY,
    ptus,
  }
}

type CreateZeroBidParams = {
  activationGroup: ActivationGroup
  deliveryDay: MarketDate
}

function createZeroBidForGroup({ activationGroup, deliveryDay }: CreateZeroBidParams): ExtendedBid {
  const volumeInWatts = 0
  const price = 0

  const ptus = generate24HourPtus(deliveryDay, volumeInWatts, price)

  return {
    portfolio: activationGroup.portfolio,
    deliveryDay,
    createdAt: DateTime.now().setZone(MARKET_TIMEZONE),
    marketProgram: activationGroup.marketProgram,
    status: Status.DRAFT,
    activationGroupUuid: activationGroup.uuid,
    bidType: BidType.CAPACITY,
    ptus,
  }
}

function generate24HourPtus(
  deliveryDay: MarketDate,
  volumeInWatts: number,
  fixedPrice: number,
  hourlyPrices?: HourlyPrices,
): ExtendedBidPtu[] {
  const startOfDay = deliveryDay.getStartOfDay()
  const ptus: ExtendedBidPtu[] = []

  for (let hour = 0; hour < HOURS_PER_DAY; hour++) {
    const ptuStart = startOfDay.plus({ hours: hour })
    const ptuEnd = startOfDay.plus({ hours: hour + 1 })

    // Use hourly price if available, otherwise fall back to fixed price
    const hourPrice = hourlyPrices ? (getPriceForHour(hourlyPrices, hour) ?? fixedPrice) : fixedPrice

    const ptuChunk: PtuChunk = {
      offeredVolume: { quantity: volumeInWatts, unit: VOLUME_UNIT },
      offeredPrice: hourPrice,
    }

    ptus.push({
      ptu: {
        start: ptuStart,
        end: ptuEnd,
      },
      offeredVolume: { quantity: volumeInWatts, unit: VOLUME_UNIT },
      ptuChunks: [ptuChunk],
    })
  }

  return ptus
}
